import type {
  IOrigin,
  TDestin,
  IDestinData,
  TCountriesList,
  TLabelsList,
  INearestAirport,
  TCategories,
  IOrigData,
  IOrigDestDataApiResponse,
  IOrigDestLabels,
  ISortedOrigins,
  ISortedDestinations,
  TDirectList
} from '@/types/OrigDest'
import { CATEGORIES, DEFAULT_ORIGIN_CODE, DIRECT_FLIGHTS } from '@/utils/constants/origDest'
import type { RouteLocationNormalizedLoaded } from 'vue-router'
import { sortByLocaleCompare } from '@/utils/localeCompare'
import { TYPE_AIRPORT } from '@/utils/constants/fsf'
import { DEFAULT_COUNTRY } from '@/utils/constants/locale'

const buildSortedOrigins = (data: IOrigData) => {
  return {
    btOrigins: sortByLocaleCompare(Object.values(data.btOrigins), 'apt'),
    nonBtOrigins: sortByLocaleCompare(Object.values(data.nonBtOrigins), 'apt')
  }
}

const buildSortedDestinations = (data: IDestinData) => {
  const result: Record<string, ISortedDestinations> = {}

  Object.entries(data).forEach(([key, value]) => {
    result[key] = {
      btDestinations: sortByLocaleCompare(Object.values(value.btDest), 'apt'),
      nonBtDestinations: sortByLocaleCompare(Object.values(value.nonBtDest), 'apt')
    }
  })

  return result
}

export const useOrigDestData = () => {
  const origDestLabels = useState<IOrigDestLabels>('origDest-labels', () => ({}))
  const destinData = useState<Partial<IDestinData>>('origDest-destin-data', () => ({}))
  const origData = useState<IOrigData>('origDest-orig-data', () => ({ btOrigins: {}, nonBtOrigins: {} }))

  const sortedOrigins = useState<ISortedOrigins>('origDest-sorted-origins', () => ({
    btOrigins: [],
    nonBtOrigins: []
  }))
  const sortedDestinations = useState<Record<string, ISortedDestinations>>('origDest-sorted-destinations', () => ({}))

  const destinationMeta = useState<
    Record<string, { labels: TLabelsList[]; countries: TCountriesList[]; direct: TDirectList[] }>
  >('origDest-destination-meta', () => ({}))
  const originCountries = computed(() => {
    const filteredCountries: TCountriesList[] = []
    const seenCountryCodes = new Set<string>()
    const originsList = Object.values(allOrigins.value)

    if (!originsList.length) return []

    originsList.forEach(({ country, countryCode }) => {
      if (!seenCountryCodes.has(countryCode)) {
        seenCountryCodes.add(countryCode)
        filteredCountries.push({
          value: country,
          key: countryCode
        })
      }
    })

    return sortByLocaleCompare(filteredCountries, 'value')
  })

  const hasSearchTerm = useState<boolean>('origDest-has-search-term', () => false)
  const selectedOrigin = useState<IOrigin | null>('origDest-selected-origin', () => null)
  const selectedDestin = useState<TDestin | null>('origDest-selected-destin', () => null)

  const selectedCategory = useState<{ origin: TCategories; destin: TCategories }>('origDest-selected-category', () => ({
    origin: CATEGORIES.ALL,
    destin: CATEGORIES.ALL
  }))
  const selectedSubCategory = useState<{ origin: string; destin: string }>('origDest-selected-sub-category', () => ({
    origin: '',
    destin: ''
  }))

  const nearestAirportCookie = useCookie<INearestAirport>('nearestAirportData')
  const nearestAirport = computed(() => {
    const airport = nearestAirportCookie.value

    return airport?.code
      ? {
          code: airport.code,
          cityCode: airport.cityCode,
          countryCode: airport.countryCode
        }
      : {
          code: DEFAULT_ORIGIN_CODE,
          cityCode: DEFAULT_ORIGIN_CODE,
          countryCode: DEFAULT_COUNTRY
        }
  })

  const origins = computed(() => {
    const allOriginsValue = allOrigins.value

    if (!Object.keys(allOriginsValue).length) {
      return { btOrigins: [], nonBtOrigins: [] }
    }

    const btOrigins = sortedOrigins.value.btOrigins
    const nonBtOrigins = sortedOrigins.value.nonBtOrigins

    if (hasSearchTerm.value) {
      return { btOrigins, nonBtOrigins }
    }

    if (selectedCategory.value.origin === CATEGORIES.COUNTRIES) {
      return {
        btOrigins: filterByCountry(btOrigins, selectedSubCategory.value.origin),
        nonBtOrigins: filterByCountry(nonBtOrigins, selectedSubCategory.value.origin)
      }
    }

    return {
      btOrigins,
      nonBtOrigins
    }
  })

  const fetchOrigDestData = async (lang: string) => {
    try {
      const data = await $fetch<IOrigDestDataApiResponse>(`/api/orig-dest/${lang}`)

      setData(data)

      const router = useRoute()
      setOriginFromUrlParameters(router)
      setDestinationFromUrlParameters(router)
    } catch (error) {
      console.error('Error loading data', error)
    }
  }

  const setData = (dataValue: IOrigDestDataApiResponse) => {
    origDestLabels.value = dataValue.origDestLabels
    origData.value = dataValue.origData
    destinData.value = dataValue.destinData

    // compute sorted copies once per dataset
    sortedOrigins.value = buildSortedOrigins(dataValue.origData)
    sortedDestinations.value = buildSortedDestinations(dataValue.destinData)
    destinationMeta.value = {}
  }

  const allOrigins = computed(() => {
    return {
      ...origData.value.btOrigins,
      ...origData.value.nonBtOrigins
    }
  })

  const ensureOrigDestData = async (lang: string) => {
    if (Object.keys(allOrigins.value).length) return

    await fetchOrigDestData(lang)
  }

  const getDefaultDestinationCategory = (): TCategories => {
    return destinationLabelsAndCountries.value.direct.length ? CATEGORIES.DIRECT : CATEGORIES.ALL
  }

  const resetCategories = () => {
    selectedCategory.value = { origin: CATEGORIES.ALL, destin: getDefaultDestinationCategory() }
    selectedSubCategory.value = { origin: '', destin: '' }
  }

  const resetCategoriesDestin = () => {
    selectedCategory.value.destin = getDefaultDestinationCategory()
    selectedSubCategory.value.destin = ''
  }

  const originKey = computed(() =>
    selectedOrigin.value ? selectedOrigin.value.code + selectedOrigin.value.type : null
  )
  const destinKey = computed(() =>
    selectedDestin.value ? selectedDestin.value.code + selectedDestin.value.type : null
  )

  const allOriginDestinations = (originKey: string): Record<string, TDestin> => {
    const destinations = destinData.value[originKey] || { btDest: {}, nonBtDest: {} }
    const mergedDestinations: Record<string, TDestin> = {}

    Object.assign(mergedDestinations, destinations.btDest, destinations.nonBtDest)

    return mergedDestinations
  }

  const currentDestinations = computed(() => {
    if (!originKey.value) {
      return {
        btDestinations: [],
        nonBtDestinations: []
      }
    }

    return (
      sortedDestinations.value[originKey.value] ?? {
        btDestinations: [],
        nonBtDestinations: []
      }
    )
  })

  const selectedDestinAvailableForOrigin = computed(() => {
    if (!selectedDestin.value) {
      return true
    }

    if (!originKey.value) {
      return false
    }

    const destinationKey = `${selectedDestin.value.code}${selectedDestin.value.type}`

    return !!allOriginDestinations(originKey.value)[destinationKey]
  })

  const canSwapOrigDest = computed(() => {
    if (!destinKey.value || !originKey.value) return false

    const newDestination: Record<string, TDestin> = allOriginDestinations(destinKey.value)

    return !!newDestination?.[originKey.value]
  })

  const swapOrigDest = () => {
    if (!destinKey.value || !originKey.value || !canSwapOrigDest.value) return

    const prevOrigin = originKey.value

    selectedOrigin.value = allOrigins.value[destinKey.value] || null

    if (!selectedOrigin.value) return

    const newDestinations = allOriginDestinations(destinKey.value)

    selectedDestin.value = newDestinations?.[prevOrigin]
  }

  const filterByCountry = <T extends IOrigin | TDestin>(data: T[], countryCode: string): T[] => {
    return data.filter((item) => item.countryCode === countryCode)
  }

  const filterByLabels = (data: TDestin[], label: string) => {
    return data.filter((item) => item.labels?.includes(label))
  }

  const currentDestinationsList = computed(() => {
    if (!originKey.value) {
      return []
    }

    return sortByLocaleCompare(Object.values(allOriginDestinations(originKey.value)), 'apt')
  })

  const directDestinations = computed(() => {
    return currentDestinationsList.value.filter((item) => item.hasBTDirect === true)
  })

  const connectingDestinations = computed(() => {
    return currentDestinationsList.value.filter((item) => item.hasBTDirect !== true)
  })

  const destinations = computed(() => {
    const { btDestinations, nonBtDestinations } = currentDestinations.value

    if (!btDestinations.length && !nonBtDestinations.length) {
      return { btDestinations: [], nonBtDestinations: [] }
    }

    if (hasSearchTerm.value) {
      return { ...currentDestinations.value }
    }

    if (selectedCategory.value.destin === CATEGORIES.COUNTRIES) {
      return {
        btDestinations: filterByCountry(btDestinations, selectedSubCategory.value.destin),
        nonBtDestinations: filterByCountry(nonBtDestinations, selectedSubCategory.value.destin)
      }
    }

    if (selectedCategory.value.destin === CATEGORIES.INTERESTS) {
      return {
        btDestinations: filterByLabels(btDestinations, selectedSubCategory.value.destin),
        nonBtDestinations: filterByLabels(nonBtDestinations, selectedSubCategory.value.destin)
      }
    }

    return { ...currentDestinations.value }
  })

  const getUniqueDestinationsLabels = (labelsList: Set<string>) => {
    const list: Record<string, TLabelsList> = {}

    Object.entries(origDestLabels.value).forEach(([key, value]) => {
      if (labelsList.has(key)) {
        list[key] = {
          value,
          key
        }
      }
    })

    return list
  }

  const buildDestinationMeta = (originKey: string) => {
    const value = destinData.value[originKey] || null

    if (!value) {
      return { labels: [], countries: [], direct: [] }
    }

    const labelsWithDestinations = new Set<string>()
    const filteredCountries: TCountriesList[] = []
    const countryCodesWithDestinations = new Set<string>()
    const directCountries: TDirectList[] = []
    let hasDirectFlights = false

    const pushCountry = (country: string, code: string) => {
      if (!countryCodesWithDestinations.has(code)) {
        countryCodesWithDestinations.add(code)
        filteredCountries.push({
          value: country,
          key: code
        })
      }
    }

    const processDestination = (dest: TDestin) => {
      dest.labels?.forEach((label) => {
        if (label === DIRECT_FLIGHTS) {
          hasDirectFlights = true
          return
        }
        labelsWithDestinations.add(label)
      })
      pushCountry(dest.country, dest.countryCode)
    }

    Object.values(value.btDest).forEach(processDestination)
    Object.values(value.nonBtDest).forEach(processDestination)

    const labelsMap = getUniqueDestinationsLabels(labelsWithDestinations)

    const sortedLabels = Object.keys(labelsMap).length ? sortByLocaleCompare(Object.values(labelsMap), 'value') : []

    if (hasDirectFlights) {
      directCountries.push({
        key: DIRECT_FLIGHTS,
        value: DIRECT_FLIGHTS
      })
    }

    const sortedCountries = filteredCountries.length ? sortByLocaleCompare(filteredCountries, 'value') : []
    const sortedDirectCountries = directCountries.length ? sortByLocaleCompare(directCountries, 'value') : []

    return {
      direct: sortedDirectCountries,
      labels: sortedLabels,
      countries: sortedCountries
    }
  }

  const destinationLabelsAndCountries = computed(() => {
    if (!originKey.value) {
      return { labels: [], countries: [], direct: [] }
    }

    if (!destinationMeta.value[originKey.value]) {
      destinationMeta.value[originKey.value] = buildDestinationMeta(originKey.value)
    }

    return destinationMeta.value[originKey.value]
  })

  watch(
    [allOrigins, nearestAirport],
    ([origins, nearest]) => {
      if (!selectedOrigin.value && nearest) {
        const nearby = Object.values(origins).find((o) => o.code === nearest.code) || null
        if (nearby) selectedOrigin.value = nearby
      }
    },
    { immediate: true }
  )

  watch(selectedOrigin, () => {
    if (!selectedDestinAvailableForOrigin.value) {
      selectedDestin.value = null
    }

    resetCategoriesDestin()
  })

  const setOriginFromUrlParameters = (router: RouteLocationNormalizedLoaded) => {
    const originCode = (router.query?.originCode as string) || (router.query?.origin as string)

    if (!originCode) {
      return
    }

    selectedOrigin.value = allOrigins.value[`${originCode}${TYPE_AIRPORT}`] ?? selectedOrigin.value
  }

  const setDestinationFromUrlParameters = (router: RouteLocationNormalizedLoaded) => {
    const destinationCode = (router.query?.destinCode as string) || (router.query?.destin as string)

    if (!destinationCode || !selectedOrigin.value) {
      return
    }

    const originKey = `${selectedOrigin.value.code}${TYPE_AIRPORT}`
    const destinations = destinData.value[originKey] || null

    if (destinations) {
      const destinationKey = `${destinationCode}${TYPE_AIRPORT}`
      selectedDestin.value = destinations.btDest[destinationKey] ?? destinations.nonBtDest[destinationKey]
    }
  }

  const setDataFomUrl = () => {
    const router = useRoute()
    setOriginFromUrlParameters(router)
    setDestinationFromUrlParameters(router)
  }

  return {
    origData,
    selectedOrigin,
    selectedDestin,
    selectedCategory,
    selectedSubCategory,
    origins,
    destinations,
    directDestinations,
    connectingDestinations,
    originCountries,
    destinationLabelsAndCountries,
    hasSearchTerm,
    canSwapOrigDest,
    allOrigins,
    nearestAirport,
    swapOrigDest,
    fetchOrigDestData,
    ensureOrigDestData,
    resetCategories,
    allOriginDestinations,
    setDataFomUrl
  }
}
